import { useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { usePrefersReducedMotion } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';

/**
 * Kill switch for the mobile app-mode presentation. The whole feature hangs
 * off this component: it renders the back chevron and the `.detail-app-mode`
 * marker that `tailwind.css` keys the header-hiding rules on, so flipping
 * this to false removes every trace without touching the call sites.
 */
const MOBILE_APP_MODE_ENABLED = true;

/** History state set by listing tile Links (Product/Project/ProfessionalTile). */
interface DetailTransitionState {
    detailTransition?: boolean;
}

/**
 * Native-app presentation for detail pages reached from a listing tile on
 * mobile: the site header is hidden and a floating back chevron dismisses to
 * the listing, like a pushed screen in a native app.
 *
 * Activation requires BOTH:
 * - the tile navigation's `detailTransition` history state, so plain links
 *   never trigger it, and
 * - a client-side PUSH mount, so any full document load — hard refresh,
 *   direct URL, restored tab — renders the normal page chrome even though
 *   the marker state survives in the history entry.
 *
 * The decision is captured once per mount: professional tab routes navigate
 * within the persistent layout without re-sending the state, and the
 * presentation must not pop back mid-visit.
 */
export const DetailMobileAppChrome = ({
    className,
}: {
    /** Position overrides for the chevron (e.g. to sit inside an inset tile). */
    className?: string;
}) => {
    const flagOn = useAtomValue(uiContextAtomViewTransitionsEnabled);
    // Belt and braces with the tile-side gate (tiles don't send the state
    // under reduced motion): also drops the chrome instantly if the OS
    // preference changes while the page is open.
    const prefersReducedMotion = usePrefersReducedMotion();
    const location = useLocation();
    const navigationType = useNavigationType();
    const navigate = useNavigate();
    const [arrivedFromListing] = useState(() => {
        const state = location.state as DetailTransitionState | null;
        return navigationType === 'PUSH' && Boolean(state?.detailTransition);
    });

    if (
        !MOBILE_APP_MODE_ENABLED ||
        !flagOn ||
        prefersReducedMotion ||
        !arrivedFromListing
    ) {
        return null;
    }

    return (
        <>
            <span className="detail-app-mode hidden" aria-hidden />
            <button
                type="button"
                aria-label="Back"
                onClick={() => navigate(-1)}
                className={cn(
                    'fixed top-4 left-4 z-fixed flex size-10 items-center justify-center rounded-[var(--radius-btn-m)] bg-gray-900/40 text-white backdrop-blur-md md:hidden',
                    className,
                )}
            >
                <ChevronLeft aria-hidden className="size-6" />
            </button>
        </>
    );
};
